/**
 * Field Mapper - Konverzija između camelCase i snake_case
 */

import { FILE_FIELD_MAPPING, NULLABLE_FIELDS, FileData } from '../types/apiTypes';

/**
 * Convert camelCase field names to snake_case for backend
 * Also handles nullable field conversion (empty strings to null)
 *
 * @param data - Object with camelCase keys
 * @returns Object with snake_case keys
 */
export const toSnakeCase = (data: Record<string, unknown>): Record<string, unknown> => {
  const snakeCaseObj: Record<string, unknown> = {};

  Object.keys(data).forEach((key) => {
    const snakeKey = FILE_FIELD_MAPPING[key] || key;
    const value = data[key];

    // Convert empty strings to null for nullable fields (numeric and timestamp)
    if (NULLABLE_FIELDS.includes(snakeKey) && value === '') {
      snakeCaseObj[snakeKey] = null;
    } else {
      snakeCaseObj[snakeKey] = value;
    }
  });

  return snakeCaseObj;
};

/**
 * Convert snake_case field names to camelCase for frontend
 *
 * @param data - Object with snake_case keys
 * @returns Object with camelCase keys
 */
export const toCamelCase = (data: Record<string, unknown>): Record<string, unknown> => {
  const camelCaseObj: Record<string, unknown> = {};

  // Create reverse mapping
  const reverseMapping: Record<string, string> = {};
  Object.entries(FILE_FIELD_MAPPING).forEach(([camel, snake]) => {
    reverseMapping[snake] = camel;
  });

  Object.keys(data).forEach((key) => {
    const camelKey = reverseMapping[key] || key;
    camelCaseObj[camelKey] = data[key];
  });

  return camelCaseObj;
};

/**
 * Convert FileData to database-ready format
 *
 * @param fileData - FileData object with camelCase keys
 * @returns Object ready for database insert/update
 */
export const mapFileDataToDb = (fileData: FileData): Record<string, unknown> => {
  return toSnakeCase(fileData as unknown as Record<string, unknown>);
};

/**
 * Convert database response to FileData format
 *
 * @param dbData - Database response with snake_case keys
 * @returns FileData object with camelCase keys
 */
export const mapDbToFileData = (dbData: Record<string, unknown>): FileData => {
  return toCamelCase(dbData) as unknown as FileData;
};

/**
 * Prepare FormData for file upload
 * Appends all non-null/undefined values as strings
 *
 * @param formData - FormData object to append to
 * @param data - Object with data to append
 */
export const appendToFormData = (formData: FormData, data: Record<string, unknown>): void => {
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
};
